import { useMemo } from 'react';

import { useProductCatalogProducts } from 'src/modules/homeownerApp/components/Palette/hooks/useProductCatalogProducts';
import { useAiAvailableConfigurationsQuery } from 'src/shared/graphql/generated';

import { DEFAULT_PALETTE_NAME } from '../../constants';
import type { CustomizationCategory } from '../../constants/customizationCategories';
import { useIsProductCatalogPromptsEnabled } from '../../hooks/split';
import { usePartner } from '../../hooks/usePartner';
import type {
  Aspect,
  DesignerMaterialProduct,
  DesignerPaintProduct,
  DesignerProduct,
} from '../../types';
import { getPartnerProductConfiguration } from '../Customize/partners';

import {
  aiMaterialToDesignerMaterial,
  aiPaintColorToDesignerPaintColor,
  productCatalogVariationToDesignerMaterial,
  productCatalogVariationToDesignerPaint,
} from './data';

const usePartnerProductCategory = (
  category: CustomizationCategory | undefined,
) => {
  const { partner } = usePartner();

  return getPartnerProductConfiguration(category, partner)?.category;
};

interface UseProductsOptions {
  aspect: Aspect;
  category: CustomizationCategory | undefined;
}

interface UsePaintColorsOptions extends UseProductsOptions {}

export const usePaintColors = ({ aspect, category }: UsePaintColorsOptions) => {
  const skip = !category || category.type !== 'paint';

  const isProductCatalogPromptsEnabled = useIsProductCatalogPromptsEnabled();

  // Available Configurations

  const { data: availableConfigurationsData, ...availableConfigurationsRest } =
    useAiAvailableConfigurationsQuery();

  const availableConfigurationsColors = useMemo(
    () =>
      availableConfigurationsData?.aiAvailableConfigurations?.paintColors.map(
        aiPaintColorToDesignerPaintColor,
      ) ?? [],
    [availableConfigurationsData?.aiAvailableConfigurations?.paintColors],
  );

  // Product Catalog

  const partnerCategory = usePartnerProductCategory(category);

  const [productCatalogColors, productCatalogRest] = useProductCatalogProducts({
    aspect,
    category,
    transformer: productCatalogVariationToDesignerPaint({
      spaceType: aspect,
      region: category?.region,
      ...partnerCategory,
    }),
    skip: skip || !isProductCatalogPromptsEnabled,
  });

  if (isProductCatalogPromptsEnabled) {
    return [productCatalogColors, productCatalogRest] as const;
  }

  return [availableConfigurationsColors, availableConfigurationsRest] as const;
};

export interface UseMaterialsOptions extends UseProductsOptions {
  include3dFusionMaterials?: boolean;
}

export const useMaterials = ({
  aspect,
  category,
  include3dFusionMaterials = false,
}: UseMaterialsOptions) => {
  const skip = !category || category.type !== 'material';
  const { region } = category ?? {};

  const isProductCatalogPromptsEnabled = useIsProductCatalogPromptsEnabled();

  // Available Configurations

  const { data: availableConfigurationsData, ...availableConfigurationsRest } =
    useAiAvailableConfigurationsQuery({
      skip: skip || isProductCatalogPromptsEnabled,
    });

  const availableConfigurationsMaterials = useMemo(() => {
    if (!region) return [];

    return (
      availableConfigurationsData?.aiAvailableConfigurations?.materials
        ?.map(aiMaterialToDesignerMaterial)
        ?.filter(
          material =>
            material.spaceType === aspect &&
            material.regions.includes(region) &&
            (include3dFusionMaterials || !material.is3dFusion),
        ) ?? []
    );
  }, [availableConfigurationsData, aspect, region, include3dFusionMaterials]);

  // Product Catalog

  const partnerCategory = usePartnerProductCategory(category);

  const [productCatalogMaterials, productCatalogRest] =
    useProductCatalogProducts({
      aspect,
      category,
      transformer: productCatalogVariationToDesignerMaterial({
        spaceType: aspect,
        region,
        ...partnerCategory,
      }),
      skip: skip || !isProductCatalogPromptsEnabled,
    });

  if (isProductCatalogPromptsEnabled) {
    return [productCatalogMaterials, productCatalogRest] as const;
  }

  return [
    availableConfigurationsMaterials,
    availableConfigurationsRest,
  ] as const;
};

/**
 * Returns the region names (like 'walls', 'roof', 'floor') for materials that
 * have available prompts.
 */
export const useAvailableMaterialsRegions = () => {
  const { data } = useAiAvailableConfigurationsQuery();

  return useMemo(() => {
    if (!data?.aiAvailableConfigurations?.materials) return undefined;

    const regionNames: string[] = [];

    data.aiAvailableConfigurations.materials.forEach(material => {
      if (!regionNames.includes(material.regions[0]))
        regionNames.push(material.regions[0]);
    });
    return regionNames;
  }, [data]);
};

type PaintOrMaterials =
  | DesignerPaintProduct[]
  | DesignerMaterialProduct[]
  | DesignerProduct[];

/**
 *
 * Return the items from paletteOptions that have a palette that matches the palette parameter
 */
export const usePaletteSelection = <T extends PaintOrMaterials>(
  paintOrMaterials: T,
  palette: string | null | undefined,
) =>
  useMemo(
    () =>
      (palette
        ? paintOrMaterials.filter(
            (material: DesignerPaintProduct | DesignerMaterialProduct) =>
              material.palettes.includes(palette),
          )
        : []) as T,
    [paintOrMaterials, palette],
  );

/**
 * If the currently selected palette has zero colors after loading the
 * available colors, set the palette to the first palette of the first paint
 * color
 */
export const useFirstAvailablePaletteFallback = (
  products: DesignerPaintProduct[] | DesignerMaterialProduct[],
  paletteProducts: DesignerPaintProduct[] | DesignerMaterialProduct[],
  selectedPalette: string | undefined,
  selectPalette: (palette: string) => void,
) => {
  const hasDefaultPalette = useMemo(
    () => products.some(color => color.palettes.includes(DEFAULT_PALETTE_NAME)),
    [products],
  );

  const firstPalette = products[0]?.palettes[0] as string | undefined;

  if (
    selectedPalette !== firstPalette &&
    paletteProducts.length === 0 &&
    firstPalette &&
    !hasDefaultPalette
  ) {
    selectPalette(firstPalette);
  }
};
